import {
  Ticket,
  TicketMessage,
  User,
  AuthTokens,
  AuthResponse,
  PaginatedResponse,
  TicketStats,
  Notification,
  UpdateTicketPayload,
  CannedResponse,
  Parametre,
  MailConnecteur,
  EntreeConnecteur,
  ResultatTestConnecteur,
  Attachment,
  AutomationRule,
  Brand,
  CreateTicketPayload,
  RattachementDomaineBrut,
  EmailComplet,
} from '../types';
import { parametresRequeteDossiers, type FiltresApiDossiers } from '../features/tickets/listeDossiers';
import { normaliserReponsesTypes } from '../features/tickets/reponsesTypes';
import {
  estReponseMotDePasseAChanger,
  exigerChangementMotDePasse,
  CODE_MOT_DE_PASSE_A_CHANGER,
} from '../features/auth/motDePasseObligatoire';

// ============================================
// CONFIGURATION
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// ============================================
// TYPES API
// ============================================

export interface DossierRecherche {
  id: string; ticketRef: string | null; title: string; status: string; priority: string; serialNumber: string | null; client: string | null; updatedAt: string;
}
export interface ResultatRecherche {
  dossiers: DossierRecherche[];
  clients: Array<{ id: string; displayName: string; email: string | null; customerCode: string | null; dossiers: number }>;
}

export interface AppelTelephonique {
  id: string; provider: string; direction: 'ENTRANT' | 'SORTANT'; phoneNumber: string | null; callerName: string | null; startedAt: string; durationSec: number | null;
  summary: string | null; transcript?: string | null; recordingUrl: string | null; createdAt: string;
  ticket: { id: string; ticketRef: string | null; title: string; status: string } | null;
  customer: { id: string; displayName: string; customerCode: string | null } | null;
}
export interface StatsTelephonie {
  total: number; sur30Jours: number; rattachesAUnDossier: number; dureeMoyenneSec: number; dossiersOuvertsParTelephone30Jours: number;
  parJour: Array<{ jour: string; nombre: number }>;
}
/** Ligne de `SlaConfig` (prisma) : délais en minutes ; `issueType` nul = règle générale de la priorité. */
export interface SlaConfig {
  id: string; priority: string; issueType: string | null; firstResponseTime: number; resolutionTime: number;
}

export interface ConfigurationAgentVocal {
  actif: boolean; cleConfiguree: boolean; webhookConfigure: boolean; enTeteAuth: string;
  webhookFinAppel: { methode: string; url: string; signature: string; evenement: string };
  outils: Array<{ nom: string; methode: string; url: string; description: string; corps?: Record<string, unknown> }>;
  consignesAgent: string[];
}

// --- Paramètres : détail rendu par le serveur ---------------------------

/** Ce dont un réglage a besoin pour produire un effet (voir le catalogue du serveur). */
export type DependanceParametre = 'smtp' | 'cleIa' | 'prestashop' | 'telephonie' | 'connecteurMail';
/** Uniquement des booléens : le serveur ne rend jamais de valeur ici. */
export type EtatDependances = Record<DependanceParametre, boolean>;

export interface ParametreDetaille extends Parametre {
  /** Bornes incluses des paramètres numériques. */
  min?: number;
  max?: number;
  entier?: boolean;
  dependances?: DependanceParametre[];
  /** Valeur du catalogue ; toujours null pour un secret. */
  valeurParDefaut?: string | null;
}

export interface CategorieParametres {
  nom: string;
  description: string | null;
}

/** Réponse de POST /admin/settings/ia/tester. */
export interface ResultatTestIA {
  fournisseur: string;
  modele: string;
  /** Durée de l'appel au fournisseur, en millisecondes. */
  latenceMs: number;
}

// --- Boîtes mail : détail et journal ------------------------------------

export interface MailConnecteurDetaille extends MailConnecteur {
  /** Seuls les messages reçus après cette date deviennent des dossiers. */
  syncFrom?: string | null;
  /**
   * Boîte d'envoi : les e-mails du SAV partent de cette boîte par Microsoft
   * Graph. Une seule boîte à la fois. Absent (ancien serveur) = false.
   */
  envoiActif?: boolean;
}

/** Codes d'échec de l'e-mail de test (POST /mail-connectors/:id/test-envoi). */
export type CodeErreurEnvoi = 'ENVOI_REFUSE_PERMISSION' | 'ENVOI_BOITE_INTROUVABLE' | 'ENVOI_ECHEC';

export interface ResultatTestEnvoi {
  envoye: true;
  to: string;
}

export type StatutEmailEntrant =
  | 'RECU' | 'DOSSIER_CREE' | 'RATTACHE' | 'A_QUALIFIER'
  | 'IGNORE_AUTO_REPLY' | 'IGNORE_SPAM' | 'IGNORE_INTERNE' | 'IGNORE_NON_REMIS'
  // Tri avant dossier (14/10/2026) : notification écartée par règle, e-mail écarté par l'IA.
  | 'IGNORE_NOTIFICATION' | 'ECARTE_IA'
  | 'ERREUR';

export interface EmailJournal {
  id: string;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  status: StatutEmailEntrant;
  statusReason: string | null;
  ticketId: string | null;
  ticketRef: string | null;
}

export interface JournalEmailsConnecteur {
  recents: EmailJournal[];
  aTraiter: EmailJournal[];
  compteurs: { erreurs: number; aQualifier: number };
}

export interface BilanSynchro {
  lus: number; crees: number; rattaches: number; ignores: number; doublons: number; erreurs: number; dureeMs: number;
}

/** Un compte client de klygroupe.com, tel que le serveur le présente au personnel. */
export interface CompteBoutique {
  prestashopId: number;
  email: string;
  nom: string;
  prenom: string;
  societe: string | null;
  /** Code client SAGE (CT_Num) porté par la boutique. */
  customerCode: string | null;
  /** Compte SAV auquel la connexion de ce client mènerait, annoncé seulement
   *  s'il est sûr. Indicatif : le compte à utiliser est toujours celui que
   *  rend POST …/compte-sav à la validation. */
  compteSav: { id: string; displayName: string; email: string | null } | null;
}

/** Compte SAV rendu par la création à la demande. */
export interface CompteSavBoutique {
  id: string;
  displayName: string;
  email: string | null;
  customerCode: string | null;
  /** true si le compte vient d'être créé. */
  cree: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ============================================
// GESTION DES TOKENS
// ============================================

const TOKEN_KEY = 'kly_admin_access_token';
const REFRESH_TOKEN_KEY = 'kly_admin_refresh_token';
const USER_KEY = 'kly_admin_user';

export const TokenStorage = {
  getAccessToken: (): string | null => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  getUser: (): User | null => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  setTokens: (tokens: AuthTokens): void => {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  },

  setUser: (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clear: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },
};

// ============================================
// HTTP CLIENT AVEC AUTH
// ============================================

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = TokenStorage.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const responseBody = await response.json();
      const payload = responseBody.data || responseBody;
      TokenStorage.setTokens(payload.tokens);
      return true;
    }
  } catch {
    console.warn('Échec du rafraîchissement du token');
  }

  TokenStorage.clear();
  return false;
}

/** Exporté pour les modules de features/ (ex. features/ia/api.ts). */
export async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const accessToken = TokenStorage.getAccessToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Si 401, tenter de rafraîchir le token
  if (response.status === 401 && TokenStorage.getRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = TokenStorage.getAccessToken();
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Mot de passe à changer : l'écran plein s'ouvre, la requête échoue.
    if (estReponseMotDePasseAChanger(response.status, errorData)) {
      exigerChangementMotDePasse();
      throw new ApiError(
        errorData.message || errorData.error || 'Vous devez choisir un nouveau mot de passe.',
        403,
        CODE_MOT_DE_PASSE_A_CHANGER
      );
    }
    throw new ApiError(
      errorData.message || errorData.error || 'Une erreur est survenue',
      response.status,
      errorData.code || 'UNKNOWN_ERROR'
    );
  }

  const responseBody = await response.json();

  // Gestion des réponses paginées
  if (responseBody.meta) {
    return {
      data: responseBody.data,
      ...responseBody.meta,
    } as T;
  }

  return responseBody.data !== undefined ? responseBody.data : responseBody;
}

// ============================================
// SERVICE API ADMIN
// ============================================

export const AdminApi = {
  // ==========================================
  // AUTHENTIFICATION
  // ==========================================

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || errorData.error || 'Identifiants invalides',
        response.status,
        'AUTH_FAILED'
      );
    }

    const responseBody = await response.json();
    const data: AuthResponse = responseBody.data;

    TokenStorage.setTokens(data.tokens);
    TokenStorage.setUser(data.user);
    if (data.mustChangePassword) exigerChangementMotDePasse();

    return data;
  },

  /**
   * Change le mot de passe du compte connecté. Le serveur rend de nouveaux
   * jetons (les anciens ne valent plus) : ils sont enregistrés aussitôt.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetchWithAuth<{ tokens?: AuthTokens }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (response?.tokens?.accessToken && response.tokens.refreshToken) {
      TokenStorage.setTokens(response.tokens);
    }
  },

  /**
   * Rafraîchit le jeton d'accès avec le jeton de rafraîchissement (temps réel
   * revenu d'une longue coupure). False si la session n'est plus valable.
   */
  async rafraichirSession(): Promise<boolean> {
    return refreshAccessToken();
  },

  /** Demande un lien de réinitialisation (personnel). Le serveur répond toujours de la même façon. */
  async demanderReinitialisation(email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/auth/mot-de-passe-oublie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || errorData.error || "La demande n'a pas pu être envoyée. Réessayez plus tard.",
        response.status,
        errorData.code || 'UNKNOWN_ERROR'
      );
    }
  },

  /** Pose un nouveau mot de passe à partir du jeton reçu par e-mail. Ne connecte pas. */
  async reinitialiserMotDePasse(jeton: string, motDePasse: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/auth/reinitialiser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jeton, motDePasse }),
    });
    const corps = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new ApiError(
        corps.message || corps.error || 'Ce lien a expiré ou a déjà servi.',
        response.status,
        corps.code || 'UNKNOWN_ERROR'
      );
    }
    return corps.message || 'Mot de passe modifié. Vous pouvez vous connecter.';
  },

  async getCurrentUser(): Promise<User> {
    const response = await fetchWithAuth<{ user: User }>('/auth/me');
    return response.user;
  },

  async updateProfile(data: {
    displayName?: string;
    phone?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<User> {
    const response = await fetchWithAuth<{ user: User }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Update local storage with new user data
    TokenStorage.setUser(response.user);
    return response.user;
  },

  async logout(): Promise<void> {
    const refreshToken = TokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Ignore
      }
    }
    TokenStorage.clear();
  },

  // ==========================================
  // TICKETS (Admin)
  // ==========================================

  /** Liste paginée, filtrée et triée par le serveur (limit ≤ 100). */
  async getTickets(filters: FiltresApiDossiers = {}): Promise<PaginatedResponse<Ticket>> {
    const params = parametresRequeteDossiers(filters);
    const query = params ? `?${params}` : '';
    const r = await fetchWithAuth<PaginatedResponse<Ticket>>(`/admin/tickets${query}`);
    const data = Array.isArray(r?.data) ? r.data : [];
    const limit = r?.limit || filters.limit || data.length || 1;
    const total = typeof r?.total === 'number' ? r.total : data.length;
    return {
      data,
      total,
      page: r?.page || filters.page || 1,
      limit,
      totalPages: typeof r?.totalPages === 'number' ? r.totalPages : Math.ceil(total / limit),
    };
  },

  /** Recherche globale : référence, numéro de série, client, commande, e-mail. */
  async recherche(q: string): Promise<ResultatRecherche> {
    return fetchWithAuth<ResultatRecherche>(`/admin/recherche?q=${encodeURIComponent(q)}`);
  },

  /** Les autres dossiers de la même machine. */
  async memesSerie(ticketId: string): Promise<{ serialNumber: string | null; dossiers: DossierRecherche[] }> {
    return fetchWithAuth(`/admin/tickets/${ticketId}/memes-serie`);
  },

  /**
   * Fusionne le dossier dans la cible (référence ou id). Répond 409 quand les
   * deux dossiers appartiennent à des clients différents : la requête est
   * alors renvoyée avec `confirmerClientsDifferents: true` après accord.
   */
  async fusionner(ticketId: string, cible: { cibleRef?: string; cibleId?: string; confirmerClientsDifferents?: boolean }):Promise<{ cibleId: string; cibleRef: string | null; deplaces: { messages: number; pieces: number; emails: number; appels: number } }> {
    return fetchWithAuth(`/admin/tickets/${ticketId}/fusionner`, { method: 'POST', body: JSON.stringify(cible) });
  },

  /**
   * Rattache à un compte client un dossier qui n'en a pas (contact libre,
   * e-mail reçu). Le serveur répond 409 si le dossier a déjà un compte
   * client, 400 si le compte choisi n'est pas un compte client utilisable :
   * le message de l'ApiError est alors celui du serveur.
   */
  async rattacherClient(ticketId: string, customerId: string, confirmerAdresseDifferente = false): Promise<unknown> {
    return fetchWithAuth(`/admin/tickets/${encodeURIComponent(ticketId)}/rattacher`, { method: 'POST', body: JSON.stringify({ customerId, ...(confirmerAdresseDifferente ? { confirmerAdresseDifferente: true } : {}) }) });
  },

  /**
   * Dossier « spam possible » (e-mail jugé indésirable par Microsoft) : ce
   * n'est pas un spam. La marque tombe, l'adresse de l'expéditeur
   * authentifié devient l'adresse de contact, le message est remis en boîte
   * de réception dans Outlook (`deplacement` : 'deplace', 'inutile' ou
   * 'echec'). 409 si le dossier n'est pas (ou plus) marqué.
   */
  async marquerPasSpam(ticketId: string): Promise<{ id: string; spamPossible: false; contactEmail: string | null; deplacement: 'deplace' | 'inutile' | 'echec' }> {
    return fetchWithAuth(`/admin/tickets/${encodeURIComponent(ticketId)}/pas-spam`, { method: 'POST' });
  },

  /** Dossier « spam possible » : c'est un spam. Clos sans notification. */
  async marquerSpam(ticketId: string): Promise<{ id: string; spamPossible: true; status: 'CLOSED'; dejaClos: boolean }> {
    return fetchWithAuth(`/admin/tickets/${encodeURIComponent(ticketId)}/spam`, { method: 'POST' });
  },

  /**
   * Dossier rattaché à une société par le domaine de l'expéditeur :
   * confirme la société. Route ouverte au personnel (requireStaff) ; quand
   * la confirmation relie le dossier à un compte client (compte proposé, ou
   * retrouvé par le code client), le serveur la réserve à l'encadrement et
   * répond 403 à un conseiller, comme /rattacher. Sinon, seul le nom de la
   * société est posé. Le rattachement passe à CONFIRME ; le serveur renvoie
   * le dossier complet (vue du personnel). 409 avec le code
   * ADRESSE_DIFFERENTE si l'adresse du dossier n'est pas celle du compte :
   * renvoyer avec `confirmerAdresseDifferente` après confirmation.
   */
  async confirmerRattachementDomaine(ticketId: string, confirmerAdresseDifferente = false): Promise<{ id: string; rattachementDomaine?: RattachementDomaineBrut | null; customerId?: string | null; customer?: User | null; companyName?: string | null }> {
    return fetchWithAuth(`/admin/tickets/${encodeURIComponent(ticketId)}/rattachement-domaine/confirmer`, {
      method: 'POST',
      ...(confirmerAdresseDifferente ? { body: JSON.stringify({ confirmerAdresseDifferente: true }) } : {}),
    });
  },

  /**
   * Dossier rattaché à une société par le domaine : « Ce n'est pas cette
   * société ». Ouverte à tout le personnel (requireStaff). La proposition
   * passe au statut REFUSE, rien d'autre ne change ; le serveur renvoie le
   * dossier complet (vue du personnel).
   */
  async refuserRattachementDomaine(ticketId: string): Promise<{ id: string; rattachementDomaine?: RattachementDomaineBrut | null; customerId?: string | null; customer?: User | null; companyName?: string | null }> {
    return fetchWithAuth(`/admin/tickets/${encodeURIComponent(ticketId)}/rattachement-domaine/refuser`, { method: 'POST' });
  },

  /**
   * « Pas une demande SAV » : le dossier est clos avec ce motif, sans aucune
   * notification au client, ni relance, ni automatisation (trace dans
   * l'historique). Route du personnel, à côté de pas-spam et spam ; si le
   * serveur ne la connaît pas sous /admin (404 « Route … non trouvée »),
   * elle est retentée sous /tickets.
   */
  async marquerHorsSav(ticketId: string): Promise<Record<string, unknown> | null> {
    const id = encodeURIComponent(ticketId);
    try {
      return await fetchWithAuth(`/admin/tickets/${id}/hors-sav`, { method: 'POST' });
    } catch (e) {
      if (e instanceof ApiError && e.statusCode === 404 && /^Route /.test(e.message)) {
        return fetchWithAuth(`/tickets/${id}/hors-sav`, { method: 'POST' });
      }
      throw e;
    }
  },

  // --- Courrier écarté par le tri (notifications, avis de l'IA) ---

  /** Liste paginée ; `motif` : 'REGLE', 'IA' ou vide (tout). Lue par features/courrier/courrier.ts. */
  async getCourrierEcarte(options: { motif?: string; page?: number } = {}): Promise<unknown> {
    const params = new URLSearchParams();
    if (options.motif) params.set('motif', options.motif);
    params.set('page', String(options.page && options.page > 0 ? options.page : 1));
    return fetchWithAuth(`/admin/courrier-ecarte?${params.toString()}`);
  },

  /** « Créer un dossier quand même » : dossier à qualifier depuis l'e-mail tracé. Idempotent. */
  async creerDossierDepuisCourrier(id: string): Promise<{ ticketId: string; ticketRef?: string | null }> {
    return fetchWithAuth(`/admin/courrier-ecarte/${encodeURIComponent(id)}/creer-dossier`, { method: 'POST' });
  },

  // --- Sous-plateforme téléphonie ---
  async telephonieAppels(page = 1): Promise<{ total: number; page: number; limit: number; appels: AppelTelephonique[] }> {
    return fetchWithAuth(`/admin/telephonie/appels?page=${page}&limit=25`);
  },
  async telephonieAppel(id: string): Promise<AppelTelephonique> {
    return fetchWithAuth(`/admin/telephonie/appels/${id}`);
  },
  async telephonieStats(): Promise<StatsTelephonie> {
    return fetchWithAuth('/admin/telephonie/stats');
  },
  async telephonieConfiguration(): Promise<ConfigurationAgentVocal> {
    return fetchWithAuth('/admin/telephonie/configuration');
  },

  async getTicketById(id: string): Promise<Ticket> {
    return fetchWithAuth<Ticket>(`/admin/tickets/${id}`);
  },

  async updateTicket(id: string, updates: UpdateTicketPayload): Promise<Ticket> {
    return fetchWithAuth<Ticket>(`/admin/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async getTicketStats(): Promise<TicketStats> {
    return fetchWithAuth<TicketStats>('/admin/tickets/stats');
  },

  async createTicket(data: CreateTicketPayload): Promise<Ticket> {
    return fetchWithAuth<Ticket>('/admin/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ==========================================
  // MESSAGES (Admin)
  // ==========================================

  async getTicketMessages(ticketId: string): Promise<TicketMessage[]> {
    const response = await fetchWithAuth<{ data: TicketMessage[] } | TicketMessage[]>(
      `/admin/tickets/${ticketId}/messages`
    );
    // Handle both paginated response {data: [...]} and direct array
    if (Array.isArray(response)) {
      return response;
    }
    return response.data || [];
  },

  /** E-mail d'origine complet d'un message venu d'une réponse par e-mail
   *  (historique cité compris : le fil n'en montre que le texte nouveau). */
  async getEmailComplet(ticketId: string, messageId: string): Promise<EmailComplet> {
    return fetchWithAuth<EmailComplet>(
      `/admin/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/email-complet`
    );
  },

  async sendMessage(
    ticketId: string,
    content: string,
    isInternal = false,
    attachmentIds: string[] = []
  ): Promise<TicketMessage> {
    return fetchWithAuth<TicketMessage>(`/admin/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, isInternal, attachments: attachmentIds }),
    });
  },

  // ==========================================
  // UTILISATEURS (Admin)
  // ==========================================

  async getAgents(): Promise<User[]> {
    const response = await fetchWithAuth<{ agents: User[] }>('/admin/agents');
    return response.agents || [];
  },

  async getUsers(filters?: { role?: string; status?: string; search?: string; limit?: number }): Promise<User[]> {
    const params = new URLSearchParams();
    if (filters?.role) params.set('role', filters.role);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.limit) params.set('limit', filters.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetchWithAuth<{ users: User[]; data?: User[]; pagination?: unknown } | User[]>(`/admin/users${query}`);

    // Handle different response formats
    if (Array.isArray(response)) return response;

    // Backend returns { users, pagination }
    if (response && typeof response === 'object') {
      if ('users' in response && Array.isArray(response.users)) {
        return response.users;
      }
      if ('data' in response && Array.isArray(response.data)) {
        return response.data;
      }
    }

    console.warn('[API] Unexpected getUsers response format:', response);
    return [];
  },

  /**
   * Personnel (administrateurs, superviseurs, agents), actifs ou non.
   * Le backend ne filtre que sur un rôle à la fois et renvoie 20 comptes par
   * défaut, tous rôles confondus : sans ce filtre, les comptes clients
   * repoussaient le personnel hors de la liste.
   */
  async getStaffUsers(limit = 200): Promise<User[]> {
    const roles = ['ADMIN', 'SUPERVISOR', 'AGENT'];
    const lots = await Promise.all(roles.map(role => AdminApi.getUsers({ role, limit })));
    const vus = new Set<string>();
    return lots.flat().filter(u => {
      if (vus.has(u.id)) return false;
      vus.add(u.id);
      return true;
    });
  },

  async getUserById(userId: string): Promise<User> {
    const response = await fetchWithAuth<{ user: User }>(`/admin/users/${userId}`);
    return response.user;
  },

  async createUser(data: {
    email: string;
    displayName: string;
    password?: string;
    role: string;
    phone?: string;
  }): Promise<User> {
    const response = await fetchWithAuth<{ user: User }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.user;
  },

  async updateUser(
    userId: string,
    data: {
      displayName?: string;
      role?: string;
      isActive?: boolean;
      phone?: string;
      password?: string;
      /** Identifiant du compte klygroupe.com (id_customer) à relier : vérifié
       *  par le serveur dans la boutique (compte actif, même adresse). */
      prestashopCustomerId?: number;
    }
  ): Promise<User> {
    const response = await fetchWithAuth<{ user: User }>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.user;
  },

  async deleteUser(userId: string): Promise<void> {
    await fetchWithAuth<void>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  },

  async getAgentStats(agentId: string): Promise<{
    totalTickets: number;
    resolvedTickets: number;
    avgResolutionTime: number;
    activeTickets: number;
    slaComplianceRate: number;
  }> {
    const response = await fetchWithAuth<{ stats: unknown }>(`/admin/users/${agentId}/stats`);
    return response.stats as {
      totalTickets: number;
      resolvedTickets: number;
      avgResolutionTime: number;
      activeTickets: number;
      slaComplianceRate: number;
    };
  },

  // ==========================================
  // CLIENTS SAV (Admin)
  // ==========================================

  async getClientsWithTickets(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    clients: Array<{
      id: string;
      email: string;
      phone: string | null;
      displayName: string;
      createdAt: string;
      lastSeenAt: string | null;
      isActive: boolean;
      prestashopCustomerId?: number | null;
      totalTickets: number;
      openTickets: number;
      lastTicket: {
        id: string;
        ticketNumber: number;
        title: string;
        status: string;
        priority: string;
        createdAt: string;
      } | null;
      recentTickets: Array<{
        id: string;
        ticketNumber: number;
        title: string;
        status: string;
        priority: string;
        createdAt: string;
        updatedAt: string;
      }>;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `/admin/clients${queryString ? `?${queryString}` : ''}`;

    return fetchWithAuth(url);
  },

  /**
   * Clients de klygroupe.com (décision du 16/10/2026) : recherche dans la
   * boutique (2 à 100 caractères), avec le compte SAV déjà lié s'il existe.
   * 503 si la boutique est injoignable ou non configurée ; 429 doux
   * (RECHERCHE_REMPLACEE) si une recherche plus récente du même utilisateur
   * l'a remplacée. `signal` : annule la requête (frappe suivante).
   */
  async rechercherClientsBoutique(q: string, options: { signal?: AbortSignal } = {}): Promise<CompteBoutique[]> {
    const r = await fetchWithAuth<CompteBoutique[]>(`/admin/clients-boutique?q=${encodeURIComponent(q)}`, options.signal ? { signal: options.signal } : {});
    return Array.isArray(r) ? r : [];
  },

  /**
   * Compte SAV d'un client de la boutique, créé à la demande s'il n'existe
   * pas (compte client sans mot de passe, relié à son compte klygroupe.com).
   * Idempotent. 404 si le compte de la boutique est absent ou inactif, 409
   * si la liaison automatique est impossible (message du serveur).
   */
  async creerCompteSavBoutique(prestashopId: number): Promise<CompteSavBoutique> {
    return fetchWithAuth<CompteSavBoutique>(`/admin/clients-boutique/${encodeURIComponent(String(prestashopId))}/compte-sav`, { method: 'POST' });
  },

  // ==========================================
  // NOTIFICATIONS (Admin)
  // ==========================================

  async getNotifications(): Promise<Notification[]> {
    const response = await fetchWithAuth<{ data: Notification[] } | Notification[]>(
      '/admin/notifications'
    );
    if (Array.isArray(response)) {
      return response;
    }
    return response.data || [];
  },

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await fetchWithAuth<void>('/admin/notifications/read', {
      method: 'PUT',
      body: JSON.stringify({ notificationIds: [notificationId] }),
    });
  },

  async markNotificationsAsRead(notificationIds: string[]): Promise<void> {
    if (notificationIds.length === 0) return;
    await fetchWithAuth<void>('/admin/notifications/read', {
      method: 'PUT',
      body: JSON.stringify({ notificationIds }),
    });
  },

  async markAllNotificationsAsRead(): Promise<void> {
    await fetchWithAuth<void>('/admin/notifications/read-all', {
      method: 'PUT',
    });
  },

  // ==========================================
  // RÉPONSES TYPES (Admin)
  // ==========================================

  /** Réponses types en base ; le serveur rend `{ responses }`. */
  async getCannedResponses(): Promise<CannedResponse[]> {
    const brut = await fetchWithAuth<unknown>('/admin/canned-responses');
    return normaliserReponsesTypes(brut);
  },

  /** Crée une réponse type ; le serveur rend `{ response }`. */
  async createCannedResponse(data: { title: string; content: string; tags: string[] }): Promise<CannedResponse> {
    const brut = await fetchWithAuth<{ response?: unknown }>('/admin/canned-responses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return normaliserReponsesTypes([brut?.response ?? brut])[0] ?? { id: '', ...data };
  },

  async updateCannedResponse(id: string, data: { title: string; content: string; tags: string[] }): Promise<CannedResponse> {
    const brut = await fetchWithAuth<{ response?: unknown }>(`/admin/canned-responses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return normaliserReponsesTypes([brut?.response ?? brut])[0] ?? { id, ...data };
  },

  async deleteCannedResponse(id: string): Promise<void> {
    await fetchWithAuth<void>(`/admin/canned-responses/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ==========================================
  // DÉLAIS SLA (Admin)
  // ==========================================

  /** Délais SLA en base (minutes), par priorité et éventuellement par type de problème. */
  async getSlaConfigs(): Promise<SlaConfig[]> {
    const response = await fetchWithAuth<{ configs: SlaConfig[] }>('/admin/sla');
    return response.configs || [];
  },

  /** Réservé aux administrateurs côté serveur. Valeurs en minutes, strictement positives. */
  async updateSlaConfig(id: string, data: { firstResponseTime: number; resolutionTime: number }): Promise<SlaConfig> {
    const response = await fetchWithAuth<{ config: SlaConfig }>(`/admin/sla/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.config;
  },

  // ==========================================
  // UPLOAD DE FICHIERS (Admin)
  // ==========================================

  /**
   * Envoie des fichiers. `ticketId` rattache les pièces au dossier (champ
   * `ticketId` lu par upload.controller) : sans lui, un document déposé dans
   * l'onglet Documents n'était lié à rien et disparaissait au rechargement.
   */
  async uploadFiles(files: File[], options: { ticketId?: string } = {}): Promise<Attachment[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (options.ticketId) formData.append('ticketId', options.ticketId);

    const token = TokenStorage.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.message || errorData.error || 'Erreur lors de l\'envoi du fichier', response.status, 'UPLOAD_ERROR');
    }

    const data = await response.json();
    return data.data || data.attachments || data;
  },

  async deleteAttachment(attachmentId: string): Promise<void> {
    await fetchWithAuth<void>(`/admin/upload/${attachmentId}`, {
      method: 'DELETE',
    });
  },

  // ==========================================
  // IA - Assistant intelligent
  // ==========================================

  async analyzeCustomerMessage(ticketId: string, customerMessage: string): Promise<{
    suggestedResponse: string;
    shouldEscalate: boolean;
    confidence: number;
    suggestedActions?: string[];
  }> {
    return fetchWithAuth<{
      suggestedResponse: string;
      shouldEscalate: boolean;
      confidence: number;
      suggestedActions?: string[];
    }>('/ai/analyze', {
      method: 'POST',
      body: JSON.stringify({ ticketId, customerMessage }),
    });
  },

  // ==========================================
  // SAGE API (Lecture seule - optionnel)
  // ==========================================

  /**
   * Vérifie le statut de la connexion SAGE
   */
  async getSageStatus(): Promise<{
    enabled: boolean;
    available: boolean;
    message: string;
  }> {
    try {
      const response = await fetchWithAuth<{
        enabled: boolean;
        available: boolean;
        message: string;
      }>('/sage/status');
      return response;
    } catch {
      return { enabled: false, available: false, message: 'Erreur de connexion' };
    }
  },

  /**
   * Récupère un client SAGE par son code
   */
  async getSageCustomer(customerCode: string): Promise<{
    customerCode: string;
    companyName: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
    postalCode?: string;
    city?: string;
  } | null> {
    try {
      const response = await fetchWithAuth<{
        customerCode: string;
        companyName: string;
        contactName?: string;
        email?: string;
        phone?: string;
        address?: string;
        postalCode?: string;
        city?: string;
      } | null>(`/sage/customer/${customerCode}`);
      return response;
    } catch {
      return null;
    }
  },

  /**
   * Récupère les commandes d'un client SAGE
   */
  async getSageCustomerOrders(customerCode: string): Promise<Array<{
    documentNumber: string;
    documentType: number;
    documentTypeLabel: string;
    customerCode: string;
    orderDate: string;
    deliveryDate?: string;
    reference?: string;
    totalHT: number;
    totalTTC: number;
    status: string;
  }>> {
    try {
      const response = await fetchWithAuth<Array<{
        documentNumber: string;
        documentType: number;
        documentTypeLabel: string;
        customerCode: string;
        orderDate: string;
        deliveryDate?: string;
        reference?: string;
        totalHT: number;
        totalTTC: number;
        status: string;
      }>>(`/sage/customer/${customerCode}/orders`);
      return response || [];
    } catch {
      return [];
    }
  },

  /**
   * Récupère une commande SAGE par son numéro
   */
  async getSageOrder(orderNumber: string): Promise<{
    documentNumber: string;
    documentType: number;
    documentTypeLabel: string;
    customerCode: string;
    orderDate: string;
    deliveryDate?: string;
    reference?: string;
    totalHT: number;
    totalTTC: number;
    status: string;
    lines?: Array<{
      lineNumber: number;
      productCode: string;
      productName: string;
      quantity: number;
      unitPrice: number;
      totalHT: number;
    }>;
  } | null> {
    try {
      const response = await fetchWithAuth<{
        documentNumber: string;
        documentType: number;
        documentTypeLabel: string;
        customerCode: string;
        orderDate: string;
        deliveryDate?: string;
        reference?: string;
        totalHT: number;
        totalTTC: number;
        status: string;
        lines?: Array<{
          lineNumber: number;
          productCode: string;
          productName: string;
          quantity: number;
          unitPrice: number;
          totalHT: number;
        }>;
      } | null>(`/sage/order/${orderNumber}`);
      return response;
    } catch {
      return null;
    }
  },

  /**
   * Recherche de clients SAGE
   */
  async searchSageCustomers(query: string): Promise<Array<{
    customerCode: string;
    companyName: string;
    contactName?: string;
    email?: string;
    phone?: string;
  }>> {
    try {
      if (!query || query.length < 2) return [];
      const response = await fetchWithAuth<Array<{
        customerCode: string;
        companyName: string;
        contactName?: string;
        email?: string;
        phone?: string;
      }>>(`/sage/search/customers?q=${encodeURIComponent(query)}`);
      return response || [];
    } catch {
      return [];
    }
  },

  // ==========================================
  // ASSISTANT IA OPÉRATEUR
  // ==========================================

  // Brouillon, synthèse et lecture des pièces jointes : features/ia/api.ts
  // (les anciennes suggestions /admin/ai/suggest et suggest-stream sont retirées).

  /**
   * Analyser rapidement un ticket
   */
  async analyzeTicket(ticketId: string): Promise<{
    ticketNumber: number;
    issueType: string;
    priority: string;
    sentiment: string;
    urgency: string;
    messageCount: number;
    customerMessageCount: number;
    extractedInfo: {
      serialNumber?: string;
      equipmentModel?: string;
      equipmentBrand?: string;
      errorCode?: string;
    };
    lastCustomerMessage: string | null;
  }> {
    // fetchWithAuth extracts .data automatically
    return fetchWithAuth<{
      ticketNumber: number;
      issueType: string;
      priority: string;
      sentiment: string;
      urgency: string;
      messageCount: number;
      customerMessageCount: number;
      extractedInfo: {
        serialNumber?: string;
        equipmentModel?: string;
        equipmentBrand?: string;
        errorCode?: string;
      };
      lastCustomerMessage: string | null;
    }>(`/admin/ai/analyze/${ticketId}`, {
      method: 'POST',
    });
  },

  // ==========================================
  // AI AUTOBOT (Statistiques et conversations)
  // ==========================================

  /**
   * Récupère les statistiques de l'AI AutoBot
   */
  async getAutoBotStats(): Promise<{
    ticketsHandled: number;
    ticketsResolved: number;
    avgResponseTime: string;
    satisfactionRate: number;
    currentlyActive: number;
  }> {
    return fetchWithAuth<{
      ticketsHandled: number;
      ticketsResolved: number;
      avgResponseTime: string;
      satisfactionRate: number;
      currentlyActive: number;
    }>('/ai/autobot/stats');
  },

  /**
   * Récupère les conversations IA récentes
   */
  async getAutoBotConversations(limit = 10): Promise<Array<{
    id: string;
    ticketId: string;
    ticketNumber: number;
    ticketTitle: string;
    status: 'resolved' | 'escalated' | 'active';
    messages: number;
    resolvedWithoutHuman: boolean;
    lastActivity: string;
  }>> {
    return fetchWithAuth<Array<{
      id: string;
      ticketId: string;
      ticketNumber: number;
      ticketTitle: string;
      status: 'resolved' | 'escalated' | 'active';
      messages: number;
      resolvedWithoutHuman: boolean;
      lastActivity: string;
    }>>(`/ai/autobot/conversations?limit=${limit}`);
  },

  /**
   * Chat conversationnel avec l'assistant IA global
   */
  async chatWithAI(
    message: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> = []
  ): Promise<{
    message: string;
    context: {
      totalTickets: number;
      slaBreached: number;
      unassignedCount: number;
      urgentCount: number;
    };
  }> {
    // fetchWithAuth extracts .data automatically, so we get { message, context } directly
    const response = await fetchWithAuth<{
      message: string;
      context: {
        totalTickets: number;
        slaBreached: number;
        unassignedCount: number;
        urgentCount: number;
      };
    }>('/admin/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationHistory }),
    });
    return response;
  },

  // ==========================================
  // AUTOMATION RULES
  // ==========================================

  /**
   * Get automation statistics
   */
  async getAutomationStats(): Promise<{
    totalRules: number;
    activeRules: number;
    todayExecutions: number;
    weekExecutions: number;
    autoAssignCount: number;
    notificationCount: number;
  }> {
    return fetchWithAuth('/admin/automation/stats');
  },

  /**
   * Get all automation rules
   */
  async getAutomationRules(includeInactive = true): Promise<AutomationRule[]> {
    return (await AdminApi.getAutomationRulesEtCatalogue(includeInactive)).regles;
  },

  /**
   * Règles et catalogue des déclencheurs et actions acceptés (`meta.catalogue`).
   * `fetchWithAuth` remonte les champs de `meta` au premier niveau : le
   * catalogue arrive donc en `catalogue`. Null s'il est absent.
   */
  async getAutomationRulesEtCatalogue(includeInactive = true): Promise<{ regles: AutomationRule[]; catalogue: unknown }> {
    const response = await fetchWithAuth<AutomationRule[] | { data?: AutomationRule[]; catalogue?: unknown; meta?: { catalogue?: unknown } }>(
      `/admin/automation/rules?includeInactive=${includeInactive}`
    );
    if (Array.isArray(response)) return { regles: response, catalogue: null };
    return {
      regles: Array.isArray(response?.data) ? response.data : [],
      catalogue: response?.catalogue ?? response?.meta?.catalogue ?? null,
    };
  },

  /** Catalogue seul (GET /admin/automation/catalogue) ; null si la route n'existe pas encore. */
  async getAutomationCatalogue(): Promise<unknown> {
    try {
      return await fetchWithAuth<unknown>('/admin/automation/catalogue');
    } catch {
      return null;
    }
  },

  /**
   * Get a single automation rule
   */
  async getAutomationRule(id: string): Promise<AutomationRule> {
    return fetchWithAuth(`/admin/automation/rules/${id}`);
  },

  /**
   * Create a new automation rule
   */
  async createAutomationRule(data: {
    name: string;
    description?: string;
    trigger: string;
    conditions: Array<{ field: string; operator: string; value: unknown }>;
    actions: Array<{ type: string; params?: Record<string, unknown> }>;
    isActive?: boolean;
    priority?: number;
  }): Promise<AutomationRule> {
    return fetchWithAuth('/admin/automation/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an automation rule
   */
  async updateAutomationRule(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      trigger: string;
      conditions: Array<{ field: string; operator: string; value: unknown }>;
      actions: Array<{ type: string; params?: Record<string, unknown> }>;
      isActive: boolean;
      priority: number;
    }>
  ): Promise<AutomationRule> {
    return fetchWithAuth(`/admin/automation/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete an automation rule
   */
  async deleteAutomationRule(id: string): Promise<void> {
    await fetchWithAuth(`/admin/automation/rules/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Toggle automation rule active status
   */
  async toggleAutomationRule(id: string): Promise<AutomationRule> {
    return fetchWithAuth(`/admin/automation/rules/${id}/toggle`, {
      method: 'PUT',
    });
  },

  /**
   * Get automation execution history
   */
  async getAutomationHistory(options?: {
    ruleId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    data: Array<{
      id: string;
      ruleId: string;
      ticketId: string | null;
      success: boolean;
      error: string | null;
      executedAt: string;
      rule: { id: string; name: string; trigger: string };
    }>;
    meta: { total: number; limit: number; offset: number };
  }> {
    const params = new URLSearchParams();
    if (options?.ruleId) params.set('ruleId', options.ruleId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchWithAuth(`/admin/automation/history${query}`);
  },

  // ==========================================
  // MARQUES (Base de connaissances)
  // ==========================================

  /**
   * Récupère toutes les marques (admin - inclut les inactives)
   */
  async getBrands(): Promise<Brand[]> {
    return fetchWithAuth<Brand[]>('/admin/brands');
  },

  /**
   * Récupère une marque par son ID
   */
  async getBrandById(id: string): Promise<Brand> {
    return fetchWithAuth<Brand>(`/brands/${id}`);
  },

  /**
   * Crée une nouvelle marque
   */
  async createBrand(data: {
    name: string;
    description?: string;
    logoUrl?: string;
    folderUrl?: string;
    websiteUrl?: string;
    isActive?: boolean;
  }): Promise<Brand> {
    return fetchWithAuth<Brand>('/admin/brands', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Met à jour une marque
   */
  async updateBrand(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      logoUrl: string;
      folderUrl: string;
      websiteUrl: string;
      isActive: boolean;
    }>
  ): Promise<Brand> {
    return fetchWithAuth<Brand>(`/admin/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Supprime une marque
   */
  async deleteBrand(id: string): Promise<void> {
    await fetchWithAuth<void>(`/admin/brands/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Réordonne les marques
   */
  async reorderBrands(brandIds: string[]): Promise<void> {
    await fetchWithAuth<void>('/admin/brands/reorder', {
      method: 'POST',
      body: JSON.stringify({ brandIds }),
    });
  },

  // ============================================
  // PARAMÈTRES APPLICATIFS
  // ============================================

  async getSettings(): Promise<{
    parametres: ParametreDetaille[];
    categories: string[];
    categoriesDetail?: CategorieParametres[];
  }> {
    return fetchWithAuth('/settings');
  },

  /** `valeur: null` rétablit la valeur par défaut du catalogue. */
  async updateSetting(cle: string, valeur: string | null): Promise<{ parametres: ParametreDetaille[] }> {
    return fetchWithAuth(`/settings/${encodeURIComponent(cle)}`, {
      method: 'PUT',
      body: JSON.stringify({ value: valeur }),
    });
  },

  /** Ce qui manque pour que certains réglages aient un effet (booléens seulement). */
  async getSettingsDependances(): Promise<{ dependances: EtatDependances }> {
    return fetchWithAuth('/admin/settings/dependances');
  },

  /**
   * Court appel au fournisseur d'IA avec la clé et le modèle enregistrés
   * (administrateurs seulement). `options` permet d'essayer un autre
   * fournisseur ou modèle sans l'enregistrer ; la clé ne transite jamais.
   * En cas d'échec, l'ApiError porte le code de l'erreur IA du serveur
   * (CLE_REFUSEE, QUOTA, INDISPONIBLE, REQUETE, DELAI, REPONSE_VIDE,
   * JSON_INVALIDE) et un message en français.
   */
  async testerCleIA(options: { fournisseur?: string; modele?: string } = {}): Promise<ResultatTestIA> {
    return fetchWithAuth('/admin/settings/ia/tester', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  // ============================================
  // CONNECTEURS DE BOÎTE MAIL
  // ============================================

  async getMailConnectors(): Promise<{ connecteurs: MailConnecteurDetaille[] }> {
    return fetchWithAuth('/mail-connectors');
  },

  /** Derniers e-mails lus sur la boîte, et ceux en erreur ou à qualifier. */
  async getMailConnectorEmails(id: string, limite = 15): Promise<JournalEmailsConnecteur> {
    return fetchWithAuth(`/admin/mail-connectors/${encodeURIComponent(id)}/emails?limite=${limite}`);
  },

  async createMailConnector(data: EntreeConnecteur): Promise<{ connecteur: MailConnecteur }> {
    return fetchWithAuth('/mail-connectors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateMailConnector(
    id: string,
    data: Partial<EntreeConnecteur>,
  ): Promise<{ connecteur: MailConnecteur }> {
    return fetchWithAuth(`/mail-connectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteMailConnector(id: string): Promise<void> {
    await fetchWithAuth(`/mail-connectors/${id}`, { method: 'DELETE' });
  },

  async setMailConnectorActive(id: string, actif: boolean): Promise<{ connecteurs: MailConnecteurDetaille[] }> {
    return fetchWithAuth(`/mail-connectors/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ actif }),
    });
  },

  /**
   * Fait de cette boîte la boîte d'envoi (true) ou la retire (false). Le
   * serveur remet les autres boîtes à false : recharger la liste ensuite.
   * PATCH selon le contrat ; si le serveur ne connaît pas encore cette route,
   * repli sur PUT, qui accepte aussi une modification partielle.
   */
  async setMailConnectorEnvoi(id: string, envoiActif: boolean): Promise<{ connecteur: MailConnecteur }> {
    const chemin = `/mail-connectors/${encodeURIComponent(id)}`;
    const body = JSON.stringify({ envoiActif });
    try {
      return await fetchWithAuth(chemin, { method: 'PATCH', body });
    } catch (e) {
      const routeAbsente = e instanceof ApiError && e.statusCode === 404 && /^Route non trouvée/i.test(e.message);
      if (!routeAbsente) throw e;
      return fetchWithAuth(chemin, { method: 'PUT', body });
    }
  },

  /**
   * Envoie un e-mail de test depuis la boîte, par Microsoft Graph. Sans
   * destinataire, le serveur l'adresse à l'utilisateur connecté. En cas
   * d'échec, l'ApiError porte un CodeErreurEnvoi.
   */
  async testMailConnectorEnvoi(id: string, to?: string): Promise<ResultatTestEnvoi> {
    const destinataire = to?.trim();
    return fetchWithAuth(`/mail-connectors/${encodeURIComponent(id)}/test-envoi`, {
      method: 'POST',
      body: JSON.stringify(destinataire ? { to: destinataire } : {}),
    });
  },

  /** Vérifie que les identifiants lisent réellement la boîte. */
  async testMailConnector(id: string): Promise<ResultatTestConnecteur> {
    return fetchWithAuth(`/mail-connectors/${id}/test`, { method: 'POST' });
  },

  /** Lit la boîte maintenant et rend le bilan (lus, créés, rattachés…). */
  async syncMailConnector(id: string): Promise<{
    bilan: BilanSynchro | null;
    motif?: string;
    /** Passe menée à terme mais fin anormale : des messages ont pu être lus. */
    avertissement?: string;
  }> {
    return fetchWithAuth(`/mail-connectors/${id}/sync`, { method: 'POST' });
  },
};

export default AdminApi;
